import { IAuth } from 'customTypes';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeFromYYYYMMDDHHMMSS, todayYYYYMMDD } from '../../utils/formatDate';
import { getAgeFromCertainDate } from '../../utils/getAge';
import ImageRenderer from '../ImageRenderer.tsx';
import { BtnGhost } from '../Button';
import TuiGrid from '../Table/TuiGrid';
import Portal from '../Portal';
import InfoWorkerModal from '../Modal/InfoWorkerModal';
import i18n from '../../translation/i18n';
import EnrollmentAttendModal from '../Modal/EnrollmentAttendModal';
import useTodayDate from '../../hooks/useTodayDate';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { DayPicker } from './PeriodPicker';
import { useRecoilValue } from 'recoil';
import { userState } from '../../atoms';
import Thumbnail from '../Modal/Thumbnail';
import ImageBtnRenderer from '../ImageBtnRenderer';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import { PulseLoader } from 'react-spinners';

// 신규 근로자
export const NewWorker = ({ name, openModal, setOpenModal, dCd }: { name: string; openModal: any; setOpenModal: any; dCd: string }) => {
  const userInfo = useRecoilValue(userState);
  const { t } = useTranslation();
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const today = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(today);
  const {
    data: newWorkerData,
    isLoading: newWorkerLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/worker',
    req: { wWorkstatus: 'I', wJobdate1: selectedDayString, wJobdate2: selectedDayString },
    queryKey: `${selectedDayString}_${dCd}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({ createAuth: false, updateAuth: false, deleteAuth: false, excelAuth: false, printAuth: false });
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  // const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');

  useEffect(() => {
    setRowKey('');
  }, [selectedDay]);

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'S002' && el.mdCd === '001' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  useEffect(() => {
    if (newWorkerData) {
      const newArray = newWorkerData.workerList?.map((el: any) => {
        const valueOfAge = getAgeFromCertainDate(el.wBdate, selectedDay);
        const wNameFormatted = `${el.wName?.substring(0, 1)}*${el.wName?.substring(2)}`;
        const wJobtypeDiv = el.wJobtypeName ? `<div>${el.wJobtypeName}</div>` : '';

        const badgeOfAge = `<span class="badge">${valueOfAge}${t('세')}</span>`;
        const isOldAge = Number(userInfo.elderlyAgeSet) <= valueOfAge;
        const badgeOfOlder = isOldAge ? `<span class="badge red">${t('고령')}</span>` : '';
        const noBirthData = '<span class="disabled" >생년월일 미입력</span>';

        const wBdateSpan = el.wBdate ? `${badgeOfAge} ${badgeOfOlder}` : noBirthData;

        return {
          ...el,
          aImg: el.wImg ? `${el.wImg}?v=${el.wCd}_${el.wImgedate}` : '',
          wNamewJobtype: `${wNameFormatted}${wJobtypeDiv}`,
          sjName: el.sjName,
          wBdatewAge: `${el.wBdate ? el.wBdate : ''}${wBdateSpan}`,
          wJobdate: el.wJobdate,
        };
      });
      setTableState(newArray);
    }
  }, [newWorkerData]);

  useEffect(() => {
    setColumns([
      {
        header: t('등록 사진'),
        name: 'aImg',
        align: 'center',
        renderer: {
          type: ImageRenderer,
        },
        width: 80,
      },
      {
        header: t('근로자명/직종'),
        name: 'wNamewJobtype',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('협력업체명'),
        name: 'sjName',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('생년월일/연령'),
        name: 'wBdatewAge',
        sortable: true,
        minWidth: 180,
        renderer: { classNames: ['line-2-birth'] },
      },
      {
        header: t('취업일자'),
        name: 'wJobdate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
    ]);
  }, [i18n.language]);

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'infoWorker', code: '' });
  };

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: dayjs().format('YYYY-MM-DD'), index: filteredIndex });
  };

  const onClickRefresh = () => refetch();

  const loading = !tableState;
  if (isError) return <IssueGuide />;
  return (
    <div className='newWorker'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {t(name)}
            <span className='badge'>{newWorkerData?.workerList.length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {newWorkerLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              {dashboardAuth.createAuth && (
                <BtnGhost onClick={onClickNewWorker}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
        {!loading && <DayPicker selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={276}
            // usePagenation
            perPage={tableState.length}
            rowHeight={56}
            onClickRow={onClickRow}
            rowKey={rowKey}
            dashboard
            setRowKey={setRowKey}
          />
        </div>
      )}

      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={0}
            getReportListAPI={onClickRefresh}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={dashboardAuth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
          />
        )}
      </Portal>
    </div>
  );
};

// 신규 고령 근로자
export const NewOldWorker = ({ name, openModal, setOpenModal, dCd }: { name: string; openModal: any; setOpenModal: any; dCd: string }) => {
  const userInfo = useRecoilValue(userState);
  const { t } = useTranslation();
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const today = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(today);
  const {
    data: newWorkerData,
    isLoading: newOldWorkerLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/worker',
    req: {
      wWorkstatus: 'I',
      oldDate1: '',
      oldDate2: String(Number(selectedDayString) - Number(`${userInfo.elderlyAgeSet}0000`)),
      wJobdate1: selectedDayString,
      wJobdate2: selectedDayString,
    },
    queryKey: `${selectedDayString}_${dCd}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({ createAuth: false, updateAuth: false, deleteAuth: false, excelAuth: false, printAuth: false });
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  // const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');

  useEffect(() => {
    setRowKey('');
  }, [selectedDay]);

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'S002' && el.mdCd === '001' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  useEffect(() => {
    if (newWorkerData) {
      const { workerList } = newWorkerData;

      const removeNoBdateWorker = workerList.filter((el: any) => el.wBdate !== null);
      const newArray = removeNoBdateWorker?.map((el: any) => {
        const valueOfAge = getAgeFromCertainDate(el.wBdate, selectedDay);
        const wNameFormatted = `${el.wName?.substring(0, 1)}*${el.wName?.substring(2)}`;
        const wJobtypeDiv = el.wJobtypeName ? `<div>${el.wJobtypeName}</div>` : '';

        const badgeOfAge = `<span class="badge">${valueOfAge}${t('세')}</span>`;
        const noBirthData = '<span class="disabled" >생년월일 미입력</span>';

        const wBdateSpan = el.wBdate ? badgeOfAge : noBirthData;

        return {
          ...el,
          aImg: el.wImg ? `${el.wImg}?v=${el.wCd}_${el.wImgedate}` : '',
          wNamewJobtype: `${wNameFormatted}${wJobtypeDiv}`,
          sjName: el.sjName,
          wBdatewAge: `${el.wBdate ? el.wBdate : ''}${wBdateSpan}`,
          wJobdate: el.wJobdate,
        };
      });
      setTableState(newArray);
    }
  }, [newWorkerData]);

  useEffect(() => {
    setColumns([
      {
        header: t('등록 사진'),
        name: 'aImg',
        align: 'center',
        renderer: {
          type: ImageRenderer,
        },
        width: 60,
      },
      {
        header: t('근로자명/직종'),
        name: 'wNamewJobtype',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('협력업체명'),
        name: 'sjName',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('생년월일/연령'),
        name: 'wBdatewAge',
        sortable: true,
        align: 'center',
        minWidth: 120,
        renderer: { classNames: ['line-2-birth'] },
      },
      {
        header: t('취업일자'),
        name: 'wJobdate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
    ]);
  }, [i18n.language]);

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'infoWorker', code: '' });
  };

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: dayjs().format('YYYY-MM-DD'), index: filteredIndex });
  };

  const onClickRefresh = () => refetch();

  const loading = !tableState;
  if (isError) return <IssueGuide />;
  return (
    <div className='newWorker'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {t(name)}
            <span className='badge'>{newWorkerData?.workerList?.filter((el: any) => el.wBdate !== null).length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {newOldWorkerLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              {dashboardAuth.createAuth && (
                <BtnGhost onClick={onClickNewWorker}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
        {!loading && <DayPicker selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={276}
            // usePagenation
            perPage={tableState.length}
            onClickRow={onClickRow}
            rowKey={rowKey}
            dashboard
            rowHeight={56}
            setRowKey={setRowKey}
          />
        </div>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={0}
            getReportListAPI={onClickRefresh}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={dashboardAuth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
          />
        )}
      </Portal>
    </div>
  );
};

// 고령 근로자
export const OldWorker = ({ name, openModal, setOpenModal, dCd }: { name: string; openModal: any; setOpenModal: any; dCd: string }) => {
  const userInfo = useRecoilValue(userState);
  const todayjs = dayjs();
  const { t } = useTranslation();
  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({ createAuth: false, updateAuth: false, deleteAuth: false, excelAuth: false, printAuth: false });
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  // const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'S002' && el.mdCd === '001' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  const {
    data: oldWorkerData,
    isLoading: oldWorkerLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/worker',
    req: {
      wWorkstatus: 'I',
      oldDate1: '',
      oldDate2: String(Number(todayjs.format('YYYYMMDD')) - Number(`${userInfo.elderlyAgeSet}0000`)),
    },
    queryKey: `${todayjs.format('YYYYMMDD')}_${dCd}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  useEffect(() => {
    if (oldWorkerData) {
      const { workerList } = oldWorkerData;

      const newArray = workerList
        ?.filter((el: any) => el.wBdate !== null)
        .map((el2: any) => {
          const valueOfAge = getAgeFromCertainDate(el2.wBdate, todayjs);
          const wNameFormatted = `${el2.wName?.substring(0, 1)}*${el2.wName?.substring(2)}`;
          const wJobtypeDiv = el2.wJobtypeName ? `<div>${el2.wJobtypeName}</div>` : '';
          const badgeOfAge = `<span class="badge">${valueOfAge}${t('세')}</span>`;

          return {
            ...el2,
            aImg: el2.wImg ? `${el2.wImg}?v=${el2.wCd}_${el2.wImgedate}` : '',
            wNamewJobtype: `${wNameFormatted}${wJobtypeDiv}`,
            sjName: el2.sjName,
            wBdatewAge: `${el2.wBdate}${badgeOfAge}`,
            wJobdate: el2.wJobdate,
          };
        });
      setTableState(newArray);
    }
  }, [oldWorkerData]);

  useEffect(() => {
    setColumns([
      {
        header: t('등록 사진'),
        name: 'aImg',
        align: 'center',
        renderer: {
          type: ImageRenderer,
        },
        width: 60,
      },
      {
        header: t('근로자명/직종'),
        name: 'wNamewJobtype',
        sortable: true,
        renderer: { classNames: ['line-2-title'] },
        minWidth: 120,
      },
      {
        header: t('협력업체명'),
        name: 'sjName',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('생년월일/연령'),
        name: 'wBdatewAge',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-birth'] },
      },
      {
        header: t('취업일자'),
        name: 'wJobdate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
    ]);
  }, [i18n.language]);

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'infoWorker', code: '' });
  };

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: dayjs().format('YYYY-MM-DD'), index: filteredIndex });
  };

  const onClickRefresh = () => refetch();

  const loading = !tableState;
  if (isError) return <IssueGuide />;

  return (
    <div className='oldWorker'>
      <div className='widget-header'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {t(name)}
            <span className='badge'>{oldWorkerData?.workerList?.filter((el: any) => el.wBdate !== null).length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {oldWorkerLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              {dashboardAuth.createAuth && (
                <BtnGhost onClick={onClickNewWorker}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={344}
            perPage={oldWorkerData?.workerList?.filter((el: any) => el.wBdate !== null).length ?? 0}
            onClickRow={onClickRow}
            rowKey={rowKey}
            dashboard
            rowHeight={56}
            setRowKey={setRowKey}
          />
        </div>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={0}
            getReportListAPI={onClickRefresh}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={dashboardAuth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
          />
        )}
      </Portal>
    </div>
  );
};

// 근로자 출역현황
export const WorkerAttend = ({
  openModal,
  setOpenModal,
  showImg,
  dCd,
  showForeignWorkerOnly = false,
}: {
  openModal: any;
  setOpenModal: any;
  showImg: boolean;
  dCd: string;
  showForeignWorkerOnly: boolean;
}) => {
  const userInfo = useRecoilValue(userState);
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const today = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(today);

  const {
    data: workerAttendDayData,
    isLoading: workerAttendDayLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/attend/day',
    req: {
      aDate1: selectedDayString,
      aDate2: selectedDayString,
    },
    queryKey: `${selectedDayString}_${dCd}_${showForeignWorkerOnly}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  const { t } = useTranslation();
  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({ createAuth: false, updateAuth: false, deleteAuth: false, excelAuth: false, printAuth: false });
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  // const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');

  const imgColumn = {
    header: t('출역 사진'),
    name: 'aImgPath',
    align: 'center',
    renderer: {
      type: ImageBtnRenderer,
      options: {
        onImgButtonClicked: (filteredIndex: number) => {
          if (!tableState || !tableState[filteredIndex]) return;
          const { aDate = '', aTime = '', fName = '', fLocationName = '', aImgPath = '' } = tableState[filteredIndex];
          setOpenModal({
            status: true,
            type: 'aImgView',
            data: { aDate, aTime, fName, fLocationName, aImgPath },
          });
        },
      },
    },
    width: 80,
  };
  const fixedSuffixColumns = [
    {
      header: t('근로자명/직종'),
      name: 'wNamewJobtype',
      sortable: true,
      minWidth: 120,
      renderer: { classNames: ['line-2-title'] },
    },
    {
      header: t('협력업체 명/위치'),
      name: 'sjName',
      sortable: true,
      minWidth: 120,
      renderer: { classNames: ['line-2-title'] },
    },
    {
      header: t('생년월일/구분'),
      name: 'wBdatewAge',
      sortable: true,
      minWidth: 180,
      renderer: { classNames: ['line-2-birth'] },
    },
    {
      header: t('출역일자/시간'),
      name: 'aTimeMax',
      align: 'center',
      sortable: true,
      minWidth: 120,
      renderer: { classNames: ['line-2-title'] },
    },
  ];

  useEffect(() => {
    const col = showImg ? [imgColumn, ...fixedSuffixColumns] : fixedSuffixColumns;
    setColumns(col);
  }, [i18n.language, tableState]);

  useEffect(() => {
    setRowKey('');
  }, [selectedDay]);

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'S003' && el.mdCd === '001' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  useEffect(() => {
    if (workerAttendDayData) {
      let filteredAttendList = [];
      if (showForeignWorkerOnly) {
        filteredAttendList = workerAttendDayData.attendList?.filter((el: any) => el.wForeignyn === 'Y');
      } else {
        filteredAttendList = workerAttendDayData.attendList;
      }
      const formattedAttendList = filteredAttendList?.map((el: any) => {
        const valueOfAge = getAgeFromCertainDate(el.wBdate, selectedDay);
        const wNameFormatted = `${el.wName?.substring(0, 1)}*${el.wName?.substring(2)}`;
        const wJobtypeDiv = el.wJobtypeName ? `<div>${el.wJobtypeName}</div>` : '';
        const fLocationNameDiv = el.fLocationName ? `<div>${el.fLocationName}</div> ` : '';

        const badgeOfAge = `<span class="badge">${valueOfAge}${t('세')}</span>`;
        const isOldAge = Number(userInfo.elderlyAgeSet) <= valueOfAge;
        const badgeOfOlder = isOldAge ? `<span class="badge red">${t('고령')}</span>` : '';
        const noBirthData = '<span class="disabled" >생년월일 미입력</span>';

        const wBdateSpan = el.wBdate ? `${badgeOfAge} ${badgeOfOlder}` : noBirthData;

        const setBullet = (color: 'red' | 'green') => {
          return `<span class="bullet ${color}"></span>`;
        };

        const goToWork = `<div class="inAndOut green">${setBullet('green')} 출근</div>`;
        const getOffWork = `<div class="inAndOut red">${setBullet('red')} 퇴근</div>`;

        const fInoutDiv = el.fInout === 'O' ? getOffWork : el.fInout ? goToWork : '';

        const aTimeMaxDiv = `<div>${el.aTimeMax}</div>`;

        return {
          ...el,
          aImgPath: el.aImg ? `${el.aImg}?v=${el.wCd}_${el.inoutDate}` : '',
          wNamewJobtype: `${wNameFormatted}${wJobtypeDiv}`,
          sjName: `${el.sjName} ${fLocationNameDiv}`,
          wBdatewAge: `${el.wBdate ? el.wBdate : ''}${wBdateSpan} ${fInoutDiv}`,
          aTimeMax: `${el.aDate}${aTimeMaxDiv}`,
          aTime: formatTimeFromYYYYMMDDHHMMSS(el.inoutDate),
        };
      });
      setTableState(formattedAttendList);
    }
  }, [workerAttendDayData]);

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'enrollmentAttend' });
  };

  useEffect(() => {
    if (openModal.type === 'infoWorker' && openModal.status === false) {
      /**
       * 수정자 : 한영광
       * 수정일자 : 2023.11.06
       * 수정내용 : 모달창 닫을 때 리패치 하게되면 rowkey 에러 발생해서 주석처리함
       *            (리패치가 꼭 필요한 경우 tuigrid 파라미터에 rowkey 삭제 할 예정)
       */
      // 근로자 출역내역 창을 닫을 때 데이터 리패치
      // onClickRefresh();
    }
  }, [openModal]);

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: rowData.aDate, index: filteredIndex });
  };

  const onClickRefresh = () => refetch();

  const loading = !tableState;
  if (isError) return <IssueGuide />;

  return (
    <div className='newWorker'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {showForeignWorkerOnly ? t('외국인 근로자 출역현황') : t('근로자 출역현황')}
            <span className='badge'>{tableState?.length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {workerAttendDayLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              {dashboardAuth.createAuth && (
                <BtnGhost onClick={onClickNewWorker}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
        {!loading && <DayPicker selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={276}
            perPage={tableState.length}
            onClickRow={onClickRow}
            rowKey={rowKey}
            rowHeight={56}
            dashboard
            setRowKey={setRowKey}
          />
        </div>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={1}
            getReportListAPI={onClickRefresh}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={dashboardAuth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
          />
        )}
        {openModal.status && openModal.type === 'enrollmentAttend' && (
          <EnrollmentAttendModal getReportListAPI={onClickRefresh} setOpenModal={setOpenModal} auth={dashboardAuth} code={{ hCd: userInfo.hCd, sCd: userInfo.sCd }} />
        )}
        {openModal.status && openModal.type === 'aImgView' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </div>
  );
};

// 고령 근로자 출역현황
export const OldWorkerAttend = ({ name, openModal, setOpenModal, dCd }: { name: string; openModal: any; setOpenModal: any; dCd: string }) => {
  const userInfo = useRecoilValue(userState);
  const { t } = useTranslation();
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const today = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(today);

  const {
    data,
    isLoading: oldWorkerAttendLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/attend/old',
    req: {
      aDate1: selectedDayString,
      aDate2: selectedDayString,
      oldDate1: '',
      oldDate2: String(Number(selectedDayString) - Number(`${userInfo.elderlyAgeSet}0000`)),
    },
    queryKey: `${selectedDayString}_${dCd}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  const [dashboardAuth, setDashboardAuth] = useState<IAuth>({ createAuth: false, updateAuth: false, deleteAuth: false, excelAuth: false, printAuth: false });
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  // const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');

  useEffect(() => {
    setColumns([
      {
        header: t('출역 사진'),
        name: 'aImgPath',
        align: 'center',
        renderer: {
          type: ImageBtnRenderer,
          options: {
            onImgButtonClicked: (filteredIndex: number) => {
              if (!tableState || !tableState[filteredIndex]) return;
              const { aDate = '', aTime = '', fName = '', fLocationName = '', aImgPath = '' } = tableState[filteredIndex];
              setOpenModal({
                status: true,
                type: 'aImgView',
                data: { aDate, aTime, fName, fLocationName, aImgPath },
              });
            },
          },
        },
        width: 80,
      },
      {
        header: t('근로자명/직종'),
        name: 'wNamewJobtype',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('협력업체 명/위치'),
        name: 'sjName',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('생년월일/구분'),
        name: 'wBdatewAge',
        sortable: true,
        minWidth: 180,
        renderer: { classNames: ['line-2-birth'] },
      },
      {
        header: t('출역일자/시간'),
        name: 'aTimeMax',
        align: 'center',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
    ]);
  }, [i18n.language, tableState]);

  useEffect(() => {
    setRowKey('');
  }, [selectedDay]);

  useEffect(() => {
    if (userInfo.userMenuList) {
      if (userInfo.gCd === 'MM') setDashboardAuth({ createAuth: true, updateAuth: true, deleteAuth: true, printAuth: true, excelAuth: true });
      else {
        const menuAuth = userInfo.userMenuList.find((el: any) => el.mtCd === 'S003' && el.mdCd === '008' && el.mrCd === '001');
        if (menuAuth !== undefined) {
          setDashboardAuth({
            createAuth: menuAuth.cYn === 'Y',
            updateAuth: menuAuth.uYn === 'Y',
            deleteAuth: menuAuth.dYn === 'Y',
            excelAuth: menuAuth.eYn === 'Y',
            printAuth: menuAuth.pYn === 'Y',
          });
        }
      }
    }
  }, [userInfo.userMenuList]);

  useEffect(() => {
    if (data) {
      const newArray = data.attendList?.map((el: any) => {
        const valueOfAge = getAgeFromCertainDate(el.wBdate, selectedDay);
        const wNameFormatted = `${el.wName?.substring(0, 1)}*${el.wName?.substring(2)}`;
        const wJobtypeDiv = el.wJobtypeName ? `<div>${el.wJobtypeName}</div>` : '';
        const fLocationNameDiv = el.fLocationName ? `<div>${el.fLocationName}</div> ` : '';

        const badgeOfAge = `<span class="badge">${valueOfAge}${t('세')}</span>`;
        const noBirthData = '<span class="disabled" >생년월일 미입력</span>';

        const wBdateSpan = el.wBdate ? badgeOfAge : noBirthData;

        const setBullet = (color: 'red' | 'green') => {
          return `<span class="bullet ${color}"></span>`;
        };

        const goToWork = `<div class="inAndOut green">${setBullet('green')} 출근</div>`;
        const getOffWork = `<div class="inAndOut red">${setBullet('red')} 퇴근</div>`;

        const fInoutDiv = el.fInout === 'O' ? getOffWork : el.fInout ? goToWork : '';

        const aTimeMaxDiv = `<div>${el.aTimeMax}</div>`;

        return {
          ...el,
          aImgPath: el.aImg ? `${el.aImg}?v=${el.wCd}_${el.inoutDate}` : '',
          wNamewJobtype: `${wNameFormatted}${wJobtypeDiv}`,
          sjName: `${el.sjName} ${fLocationNameDiv}`,
          wBdatewAge: `${el.wBdate ? el.wBdate : ''}${wBdateSpan} ${fInoutDiv}`,
          aTimeMax: `${el.aDate}${aTimeMaxDiv}`,
          aTime: formatTimeFromYYYYMMDDHHMMSS(el.inoutDate),
        };
      });
      setTableState(newArray);
    }
  }, [data]);

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'enrollmentAttend' });
  };

  useEffect(() => {
    if (openModal.type === 'infoWorker' && openModal.status === false) {
      /**
       * 수정자 : 한영광
       * 수정일자 : 2023.11.06
       * 수정내용 : 모달창 닫을 때 리패치 하게되면 rowkey 에러 발생해서 주석처리함
       *            (리패치가 꼭 필요한 경우 tuigrid 파라미터에 rowkey 삭제 할 예정)
       */
      // 근로자 출역내역 창을 닫을 때 데이터 리패치
      // onClickRefresh();
    }
  }, [openModal]);

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: rowData.aDate, index: filteredIndex });
  };

  const onClickRefresh = () => refetch();

  const loading = !tableState;
  if (isError) return <IssueGuide />;

  return (
    <div className='newWorker'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {t(name)}
            <span className='badge'>{data?.attendList.length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {oldWorkerAttendLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              {dashboardAuth.createAuth && (
                <BtnGhost onClick={onClickNewWorker}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
        {!loading && <DayPicker selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={276}
            perPage={tableState.length}
            onClickRow={onClickRow}
            rowHeight={56}
            dashboard
            setRowKey={setRowKey}
          />
        </div>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={1}
            getReportListAPI={onClickRefresh}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={dashboardAuth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
          />
        )}
        {openModal.status && openModal.type === 'enrollmentAttend' && (
          <EnrollmentAttendModal getReportListAPI={onClickRefresh} setOpenModal={setOpenModal} auth={dashboardAuth} code={{ hCd: userInfo.hCd, sCd: userInfo.sCd }} />
        )}
        {openModal.status && openModal.type === 'aImgView' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </div>
  );
};

// 방문자 출입현황
export const VisitorList = ({ openModal, setOpenModal, showImg, dCd }: { openModal: any; setOpenModal: any; showImg: boolean; dCd: string }) => {
  const userInfo = useRecoilValue(userState);
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const today = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(today);
  const { eExcelYn, eListYn } = userInfo;
  const {
    data: visitorListData,
    isLoading: visitorListLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/visit',
    req: {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      aDate1: selectedDayString,
      aDate2: selectedDayString,
    },
    queryKey: `${selectedDayString}_${dCd}`,
    enabledKey: userInfo.sCd !== '00000',
  });

  const { t } = useTranslation();
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[] | null>(null);
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [rowKey, setRowKey] = useState('');
  const {
    data: siteSetting,
    isLoading,
    isError: isErrorSiteSetting,
  } = useQuery(['edutermSetting', userInfo.sCd], () => getVisitSettingAPI(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
  });

  const getVisitSettingAPI = async () => {
    const { hCd, sCd } = userInfo;
    try {
      const res = await apiGet({ path: '/visit/setting', contentType: 'application/json', req: { hCd, sCd } });
      const { photoYn, carnumYn, visitHnumYn, visitBdateYn } = res.data.data;
      return { photoYn: photoYn === 'Y', carnumYn: carnumYn === 'Y', visitHnumYn: visitHnumYn === 'Y', visitBdateYn: visitBdateYn === 'Y' };
    } catch (err) {
      console.error('err : ', err);
      throw new Error('Error');
    }
  };

  useEffect(() => {
    const newColumns = [];

    if (siteSetting?.photoYn) {
      newColumns.push({
        header: t('사진'),
        name: 'vUrl',
        align: 'center',
        renderer: {
          type: ImageRenderer,
        },
        width: 80,
      });
    }

    const baseColumns = [
      {
        header: t('방문자명'),
        name: 'vName',
        sortable: true,
        maskingType: 'name',
        isPrivate: true,
        minWidth: 60,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('방문부서'),
        name: 'dept',
        sortable: true,
        minWidth: 120,
        renderer: { classNames: ['line-2-title'] },
      },
      {
        header: t('방문목적'),
        name: 'purpose',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['line-2-birth'] },
      },
      // {
      //   header: t('차량번호'),
      //   name: 'carNum',
      //   align: 'center',
      //   sortable: true,
      //   minWidth: 120,
      // },
    ];

    newColumns.push(...baseColumns);

    if (siteSetting?.visitBdateYn) {
      newColumns.push({
        header: t('생년월일'),
        name: 'vBdate',
        isPrivate: true,
        maskingType: 'birth',
        align: 'center',
        sortable: true,
        minWidth: 120,
      });
    }

    // if (siteSetting?.visitHnumYn) {
    //   newColumns.push({
    //     header: t('연락처'),
    //     name: 'vTel',
    //     align: 'center',
    //     isPrivate: true,
    //     maskingType: 'tel',
    //     sortable: true,
    //     minWidth: 120,
    //   });
    // }

    newColumns.push({
      header: t('방문일자'),
      name: 'logDate',
      align: 'center',
      sortable: true,
      minWidth: 120,
      renderer: { classNames: ['line-2-title'] },
    });

    setColumns(newColumns);
  }, [i18n.language, siteSetting?.photoYn, siteSetting?.visitBdateYn]);

  useEffect(() => {
    setRowKey('');
  }, [selectedDay]);

  useEffect(() => {
    if (visitorListData) {
      const newArray = visitorListData?.map((el: any) => {
        const dateTimeDiv = `${el.logDate?.split(' ')[0]} <div>${el.logDate?.split(' ')[1]}</div>`;

        return {
          ...el,
          vUrl: el.vUrl ? `${el.vUrl}?v=${el.logCd}_${el.inoutDate}` : '',
          logDate: dateTimeDiv,
        };
      });
      setTableState(newArray);
    }
  }, [visitorListData]);

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  // 방문자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({
      status: true,
      type: 'imgView',
      data: { aImgPath: rowData.vUrl },
      thumbnailOnly: true,
    });
  };

  const onClickRefresh = () => refetch();

  if (isError || isErrorSiteSetting) return <IssueGuide />;
  const loading = !tableState || isLoading;

  return (
    <div className='newWorker'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>
            {t('방문자 출입현황')}
            <span className='badge'>{visitorListData?.length ?? 0}</span>
          </span>
          <div className='widget-top-btn-wrap'>
            {visitorListLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-primary)' }}>
                <PulseLoader size={6} color='var(--text-primary)' />
                <span>{t('불러오는중')}</span>
              </div>
            )}
            <div className='widget-btn-group'>
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        </div>
        {!loading && <DayPicker selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <div className='tuiGridWrapper scroll'>
          <TuiGrid
            data={tableState}
            filterTableState={filterTableState}
            setFilterTableState={setFilterTableState}
            columns={columns}
            height={276}
            perPage={tableState.length}
            onClickRow={onClickRow}
            rowKey={rowKey}
            rowHeight={56}
            dashboard
            setRowKey={setRowKey}
            eListYn={eListYn}
            eExcelYn={eExcelYn}
          />
        </div>
      )}
      <Portal openModal={openModal?.status}>{openModal.status && openModal.type === 'imgView' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </div>
  );
};
